import { useState } from "react";
import { ContextMenu } from "../../../common/ContextMenu";
import { buildCanonicalSafetyFormPath } from "../../../../services/SafetyFormRouteUtils";
import { useTableSort } from "../../../landingPage/components/del_table/useTableSort";
import { SortableHeader } from "../../../landingPage/components/del_table/SortableHeader";
import styles from "../shared/safety-table-common.module.css";
import reportStyles from "./SafetyFormReportingTab.module.css";

export const NearMissReportingTable = ({
  forms,
  onRowSelect,
  onDoubleClick,
  selectedFormId,
  selectedIds = [],
  onToggleSelect,
  onDownloadSingle,
  clearSelectedIds,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const { sortedItems, sortColumn, sortDirection, onSort } = useTableSort(forms);

  const handleContextMenu = (e, form) => {
    e.preventDefault();
    onRowSelect(form);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      form: form,
    });
  };

  const getNearMissUrl = (form) => {
    return buildCanonicalSafetyFormPath({
      tab: "safety_form_reporting",
      formType: "near_miss",
      nearMissId: form.id,
      action: "view",
      open: true,
    });
  };

  const handleOpenInNewTab = (form) => {
    const url = getNearMissUrl(form);
    window.open(window.location.origin + url, "_blank");
  };

  const handleCopyLink = (form) => {
    const url = window.location.origin + getNearMissUrl(form);
    navigator.clipboard.writeText(url);
  };

  const getContextMenuOptions = (form) => {
    const options = [
      {
        label: "Open in new tab",
        action: () => handleOpenInNewTab(form),
      },
      {
        label: "Copy link",
        action: () => handleCopyLink(form),
      },
    ];

    // Add PDF download option
    if (onDownloadSingle) {
      options.push({
        label: "Download PDF",
        action: () => onDownloadSingle(form.id),
      });
    }

    return options;
  };

  return (
    <div className={styles.nearMissTableContainer} id="nearMissReportingTable">
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {forms.map((form) => (
            <div
              key={`card-${form.id}`}
              onClick={() => onRowSelect(form)}
              onDoubleClick={() => onDoubleClick(form)}
              onContextMenu={(e) => handleContextMenu(e, form)}
              className={`${styles.card} ${
                form.id === selectedFormId ? styles.selectedCard : ""
              }`}
            >
              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Created Date:</span>
                  <span className={styles.value}>{form.created_at || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Updated Date:</span>
                  <span className={styles.value}>{form.updated_at || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Incident Date/Time:</span>
                  <span className={styles.value}>
                    {form.incident_date || "-"} {form.incident_time || ""}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Location:</span>
                  <span className={styles.value}>{form.location || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Site:</span>
                  <span className={styles.value}>{form.site || "-"}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Reported To:</span>
                  <span className={styles.value}>
                    {form.reported_to_name || "-"}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Reported By:</span>
                  <span className={styles.value}>
                    {form.reported_by_name || "-"}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Created By:</span>
                  <span className={styles.value}>
                    {form.created_by_name || "-"}
                  </span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Status:</span>
                  <span
                    className={
                      form.status === "Submitted"
                        ? reportStyles.statusSubmitted
                        : reportStyles.statusDraft
                    }
                  >
                    {form.status || "-"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={`${styles.table} ${styles.nearMissTable}`}>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    // Select up to 50 forms
                    const formIds = forms.map((f) => f.id).slice(0, 50);
                    formIds.forEach((id) => {
                      if (!selectedIds?.includes(id)) {
                        onToggleSelect?.(id);
                      }
                    });
                  } else {
                    clearSelectedIds?.();
                  }
                }}
                checked={
                  forms.length > 0 &&
                  forms.slice(0, 50).every((f) => selectedIds?.includes(f.id))
                }
              />
              PDF
            </th>
            <SortableHeader label="Created Date" column="created_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Updated Date" column="updated_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Incident Date" column="incident_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <th>Incident Time</th>
            <th>Location</th>
            <th>Site</th>
            <th>Reported To</th>
            <th>Reported By</th>
            <th>Created By</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((form) => (
            <tr
              key={`row-${form.id}`}
              onClick={() => onRowSelect(form)}
              onDoubleClick={() => onDoubleClick(form)}
              onContextMenu={(e) => handleContextMenu(e, form)}
              className={form.id === selectedFormId ? styles.selectedRow : ""}
            >
              <td>
                <input
                  type="checkbox"
                  checked={selectedIds?.includes(form.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.(form.id);
                  }}
                />
              </td>
              <td title={form.created_at}>{form.created_at || "-"}</td>
              <td title={form.updated_at}>{form.updated_at || "-"}</td>
              <td title={form.incident_date}>{form.incident_date || "-"}</td>
              <td title={form.incident_time}>{form.incident_time || "-"}</td>
              <td title={form.location}>{form.location || "-"}</td>
              <td title={form.site}>{form.site || "-"}</td>
              <td title={form.reported_to_name}>
                {form.reported_to_name || "-"}
              </td>
              <td title={form.reported_by_name}>
                {form.reported_by_name || "-"}
              </td>
              <td title={form.created_by_name}>
                {form.created_by_name || "-"}
              </td>
              <td>
                <span
                  className={
                    form.status === "Submitted"
                      ? reportStyles.statusSubmitted
                      : reportStyles.statusDraft
                  }
                >
                  {form.status || "-"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={getContextMenuOptions(contextMenu.form)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};
